import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/product.css";
import { db } from "../utils/firebaseconfig";
import { collection, getDocs, query, where } from "firebase/firestore";

function LiveAuction() {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        setLoading(true);
        const streamsRef = collection(db, "livestreams");
        // Only get streams where isLive is true
        const q = query(streamsRef, where("isLive", "==", true));
        const querySnapshot = await getDocs(q);
        const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const updated = data.map((stream) => {
          const end = new Date(stream.endTime);
          const now = new Date();
          const diff = end - now;
          let countdown = "Ended";
          let urgency = "gray";
          let status = "ended";

          if (diff > 0) {
            const min = Math.floor(diff / 60000);
            const sec = Math.floor((diff % 60000) / 1000);
            countdown = `${min}m ${sec}s left`;
            status = "live";

            if (min >= 3) urgency = "green";
            else if (min >= 1) urgency = "orange";
            else urgency = "red";
          } else {
            status = "ended";
            urgency = "gray";
          }

          return { ...stream, countdown, urgency, status };
        });

        // Sort streams by time left
        const sortedStreams = updated.sort((a, b) => {
          // Sort by status first (active streams come before ended streams)
          if (a.status === "live" && b.status !== "live") return -1;
          if (a.status !== "live" && b.status === "live") return 1;
          
          // If both are the same status, sort by creation time (newer first)
          return new Date(b.createdAt) - new Date(a.createdAt);
        });

        setStreams(sortedStreams);
      } catch (error) {
        console.error("Error fetching streams:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStreams();
    const interval = setInterval(fetchStreams, 10000); // Update every 10 seconds instead of every second
    return () => clearInterval(interval);
  }, []);

  // Function to format the countdown text
  const formatCountdown = (stream) => {
    if (stream.status === "ended") {
      return <span style={{ color: "gray" }}>Ended</span>;
    }
    
    const end = new Date(stream.endTime);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) {
      return <span style={{ color: "gray" }}>Ended</span>;
    }
    
    const min = Math.floor(diff / 60000);
    const sec = Math.floor((diff % 60000) / 1000);
    
    let color;
    if (min >= 3) color = "green";
    else if (min >= 1) color = "orange";
    else color = "red";
    
    return (
      <span style={{ color, fontWeight: "bold" }}>
        ⏰ {min}m {sec}s left
      </span>
    );
  };

  return (
    <div className="page-container">
      <h1>🔴 Live Auctions</h1>
      <p>Join active live video auctions in real-time.</p>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div className="spinner" style={{ margin: "0 auto 20px" }}></div>
          <p>Loading live auctions...</p>
        </div>
      ) : streams.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <h2 style={{ color: "gray", marginBottom: "1rem" }}>
            🚫 No active streams available
          </h2>
          <p>Check back later for new live auctions or start your own!</p>
        </div>
      ) : (
        <div className="product-grid">
          {streams.map((stream) => (
            <div
              key={stream.id}
              className={`product-card ${stream.status === "ended" ? "ended-stream" : ""}`}
              onClick={() => navigate(`/livestream/${stream.id}`)}
            >
              <div className="stream-status-badge" style={{ backgroundColor: stream.status === "live" ? "rgba(255, 0, 0, 0.8)" : "rgba(0, 0, 0, 0.6)" }}>
                {stream.status === "live" ? "🔴 LIVE" : "⏹️ ENDED"}
              </div>
              <img 
                src={stream.image} 
                alt={stream.title} 
                className="stream-card__image" 
                style={{ 
                  filter: stream.status === "ended" ? "grayscale(0.7)" : "none",
                  opacity: stream.status === "ended" ? 0.8 : 1
                }} 
              />
              <div className="product-card__details">
                <h3 className="product-card__title">{stream.title || "Untitled Stream"}</h3>
                <p className="product-card__price">
                  <span style={{ fontWeight: "normal" }}>Host:</span> {stream.host || "Anonymous"}
                </p>
                <p style={{ marginTop: 6 }}>
                  {formatCountdown(stream)}
                </p>
                {stream.status === "ended" && stream.highestBid && (
                  <p className="final-bid">
                    🏆 Final Bid: AED {stream.highestBid.amount}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LiveAuction;
